import React, { useState } from "react"
import {
	Box, CircularProgress, Table, TableBody, TableCell, TableHead,
	TablePagination, TableRow, Typography,
} from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"
import { AppButtonIcon, AppCardTitle, AppCheckCrossIcon, AppValueChip, secondaryButtonSx } from "@components"
import { dsColorNew } from "@palette"
import { useToast } from "@hooks"
import { BACKEND_APIS } from "@constants"
import useAutoEuropeWarningsCatalog from "Pages/AutoEuropeTool/Checkers/@hooks/useWarningsCatalog"
import WarningDetailModal from "Pages/AutoEuropeTool/Checkers/@components/WarningDetailModal"

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

// The card of the app's tables: white, thin border, rounded
const cardSx = { backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px' }

// Clave estable de un checker: la misma terna que usa search-export como task_id.
const rowKey = row => `${row.idReportExecution}_${row.idOfficePickup}_${row.toolId}`

// Los checkers anteriores a la columna warning_detail no traen nada que abrir.
const hasDetail = row => !!Object.keys(row.warningDetail ?? {}).length

function ReasonChip({ reason, getLabel, isInfo }) {
	const info = isInfo(reason)
	return (
		<AppValueChip tone={info ? 'accent' : 'warning'} title={reason} sx={{ mr: 0.5, mb: 0.5 }}>
			{getLabel(reason)}
		</AppValueChip>
	)
}

// Mismo criterio que la columna Exported de la vista Table: si hay fichero se
// ofrece la descarga, y si no solo el estado.
function ExportedCell({ row }) {
	if (row.exported && row.exportUrl) {
		return (
			<AppButtonIcon
				icon="file-arrow-down"
				tooltip="Download export"
				sx={{ color: dsColorNew.appBlue }}
				onClick={() => window.open(row.exportUrl, '_blank')}
			/>
		)
	}

	return <AppCheckCrossIcon condition={!!row.exported}/>
}

function RelaunchButton({ row, isRelaunchable }) {
	const { showToast } = useToast()
	const [isLoading, setIsLoading] = useState(false)

	// Se ofrece si alguno de sus motivos se arregla cambiando configuracion.
	if (!(row.warningReasons ?? []).some(isRelaunchable)) return null

	if (isLoading) return <CircularProgress size={17} sx={{ m: 0.2 }}/>

	const onClick = () => {
		setIsLoading(true)
		showToast.promise(
			postSubmit(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/check/relaunch`, {
				idReportExecution: row.idReportExecution,
				idOfficePickup: row.idOfficePickup,
				idPricingTool: row.toolId,
			}),
			{
				pending: 'Sending...',
				success: 'This execution will be checked again',
				// onFinish (no onFinally) es el hook de useToastSeeker y corre tanto
				// si resuelve como si falla: sin el, el spinner no para nunca.
				onFinish: () => setIsLoading(false),
			}
		)
	}

	return (
		<ButtonLoadingSeeker
			size="small"
			variant="outlined"
			startIcon={<IconSeeker icon="rotate-right"/>}
			onClick={onClick}
			sx={{ ...secondaryButtonSx, height: 30, px: 1.25, fontSize: 13 }}
		>
			Relaunch
		</ButtonLoadingSeeker>
	)
}

export default function WarningsSection({ rows, isFiltered }) {
	const { getLabel, getMessage, isRelaunchable, isInfo } = useAutoEuropeWarningsCatalog()
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0])
	const [detailRow, setDetailRow] = useState(null)

	// Es una pestaña propia, asi que el caso vacio se dice; devolver null aqui
	// dejaria la pantalla en blanco sin explicar por que.
	if (!rows.length) {
		return (
			<Box sx={{ ...cardSx, display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2 }}>
				<IconSeeker
					icon={isFiltered ? 'filter' : 'circle-check'}
					sx={{ fontSize: 14, color: isFiltered ? dsColorNew.textSubtle : dsColorNew.successText }}
				/>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
					{isFiltered ? 'No executions match the active filters' : 'No executions with warnings'}
				</Typography>
			</Box>
		)
	}

	// La pagina se reajusta al filtrar: si el filtro deja menos filas que el offset
	// actual la tabla saldria vacia con paginas por delante.
	const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1)
	const safePage = Math.min(page, maxPage)
	const pageRows = rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage)

	return (
		<Box sx={{ ...cardSx, p: 2, mb: 2 }}>
			<AppCardTitle icon="triangle-exclamation" title="Warnings" iconBg={dsColorNew.noteAmberBg} iconColor={dsColorNew.noteAmber} sx={{ mb: 1.5 }}>
				<AppValueChip tone="warning" sx={{ ml: 'auto' }}>{`${rows.length} executions`}</AppValueChip>
			</AppCardTitle>
			<Table
				size="small"
				sx={{
					'& tbody tr:nth-of-type(even)': { backgroundColor: dsColorNew.tableRowAlt },
					'& tbody tr:hover': { backgroundColor: dsColorNew.hoverBgLight },
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>Report</TableCell>
						<TableCell>Pricing Tool</TableCell>
						<TableCell align="center">Execution</TableCell>
						<TableCell align="center">Office</TableCell>
						<TableCell align="center">Num Notifications</TableCell>
						<TableCell align="center">Exported</TableCell>
						<TableCell>Reason</TableCell>
						<TableCell align="center">Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{pageRows.map(row => (
						<TableRow key={rowKey(row)} hover>
							<TableCell>{row.reportName ?? ''}</TableCell>
							<TableCell>{row.toolName ?? ''}</TableCell>
							<TableCell align="center">{row.executionTime ?? ''}</TableCell>
							<TableCell align="center">{row.idOfficePickupName ?? ''}</TableCell>
							<TableCell align="center">{row.numNotifications ?? 0}</TableCell>
							<TableCell align="center"><ExportedCell row={row}/></TableCell>
							<TableCell>
								{(row.warningReasons ?? []).map(r => (
									<ReasonChip key={r} reason={r} getLabel={getLabel} isInfo={isInfo}/>
								))}
							</TableCell>
							<TableCell align="center">
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
									{hasDetail(row) && <AppButtonIcon
										icon="circle-info"
										tooltip="Warning detail"
										sx={{ color: dsColorNew.appBlue }}
										onClick={() => setDetailRow(row)}
									/>}
									<RelaunchButton row={row} isRelaunchable={isRelaunchable}/>
								</Box>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			<TablePagination
				component="div"
				count={rows.length}
				page={safePage}
				onPageChange={(_, newPage) => setPage(newPage)}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
				onRowsPerPageChange={e => {
					setRowsPerPage(parseInt(e.target.value, 10))
					setPage(0)
				}}
			/>
			<WarningDetailModal
				row={detailRow}
				onClose={() => setDetailRow(null)}
				getLabel={getLabel}
				getMessage={getMessage}
				isInfo={isInfo}
			/>
		</Box>
	)
}
